import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  ShoppingCart as ShoppingCartIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

const GestionServiciosDialog = ({
  open,
  onClose,
  ordenId,
  ordenEstado,
  serviciosActuales = [],
  onServiciosUpdated
}) => {
  const [servicios, setServicios] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [nuevoServicio, setNuevoServicio] = useState({
    id_servicio: '',
    cantidad: 1,
    precio: 0,
    detalle_glosa: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  // Estados que permiten modificación: 1=Pendiente, 2=Asignado
  const puedeModificar = [1, 2].includes(parseInt(ordenEstado));

  useEffect(() => {
    if (open) {
      setServicios([...serviciosActuales]);
      fetchServiciosDisponibles();
      setError('');
      setSuccess('');
    }
  }, [open, serviciosActuales]);

  const fetchServiciosDisponibles = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/servicios`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setServiciosDisponibles(response.data);
    } catch (error) {
      setError('Error al cargar servicios disponibles');
    }
  };

  const calcularTotal = () => {
    return servicios.reduce((total, servicio) => 
      total + (servicio.cantidad * servicio.precio), 0
    );
  };

  const agregarServicio = async () => {
    if (!nuevoServicio.id_servicio || !nuevoServicio.cantidad || !nuevoServicio.precio) {
      setError('Todos los campos son requeridos');
      return;
    }

    const servicioSeleccionado = serviciosDisponibles.find(
      s => s.id_servicio === parseInt(nuevoServicio.id_servicio)
    );

    if (!servicioSeleccionado) {
      setError('Servicio no encontrado');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/detalle-ordenes`,
        {
          id_orden_servicio: ordenId,
          id_servicio: nuevoServicio.id_servicio,
          cantidad: nuevoServicio.cantidad,
          precio: nuevoServicio.precio,
          detalle_glosa: nuevoServicio.detalle_glosa
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Agregar a la lista local
      const nuevoItem = {
        ...response.data,
        nombre_servicio: servicioSeleccionado.nombre_servicio,
        descripcion_servicio: servicioSeleccionado.descripcion_servicio
      };

      setServicios([...servicios, nuevoItem]);
      setNuevoServicio({
        id_servicio: '',
        cantidad: 1,
        precio: 0,
        detalle_glosa: ''
      });
      
      setSuccess('Servicio agregado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al agregar servicio');
    } finally {
      setLoading(false);
    }
  };

  const eliminarServicio = async (servicio, index) => {
    if (!window.confirm(`¿Eliminar el servicio "${servicio.nombre_servicio}"?`)) {
      return;
    }

    setLoading(true);
    try {
      if (servicio.id_detalle) {
        await axios.delete(
          `${config.API_BASE_URL}/api/detalle-ordenes/${servicio.id_detalle}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }

      const nuevosServicios = servicios.filter((_, i) => i !== index);
      setServicios(nuevosServicios);
      
      setSuccess('Servicio eliminado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al eliminar servicio');
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicion = (index) => {
    if (!puedeModificar) {
      setError('No se pueden editar servicios en el estado actual');
      return;
    }
    setEditingIndex(index);
    setError('');
  };

  const guardarEdicion = async (index, nuevosDatos) => {
    setLoading(true);
    try {
      const servicio = servicios[index];
      
      if (servicio.id_detalle) {
        await axios.put(
          `${config.API_BASE_URL}/api/detalle-ordenes/${servicio.id_detalle}`,
          {
            id_servicio: servicio.id_servicio,
            cantidad: nuevosDatos.cantidad,
            precio: nuevosDatos.precio,
            detalle_glosa: nuevosDatos.detalle_glosa,
            id_estado_servicio: servicio.id_estado_servicio || 1
          },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }

      const serviciosActualizados = [...servicios];
      serviciosActualizados[index] = {
        ...serviciosActualizados[index],
        ...nuevosDatos
      };
      setServicios(serviciosActualizados);
      setEditingIndex(-1);
      
      setSuccess('Servicio actualizado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al actualizar servicio');
    } finally {
      setLoading(false);
    }
  };

  const cancelarEdicion = () => {
    setEditingIndex(-1);
    setError('');
  };

  const handleServicioChange = (value) => {
    setNuevoServicio({ ...nuevoServicio, id_servicio: value });
    
    // Auto-completar precio con el precio base del servicio
    const servicioSeleccionado = serviciosDisponibles.find(
      s => s.id_servicio === parseInt(value)
    );
    if (servicioSeleccionado) {
      setNuevoServicio({
        ...nuevoServicio,
        id_servicio: value,
        precio: parseFloat(servicioSeleccionado.precio)
      });
    }
  };

  const guardarCambios = () => {
    onServiciosUpdated && onServiciosUpdated();
    onClose();
  };

  const getEstadoText = () => {
    const estadosMap = {
      1: 'Pendiente',
      2: 'Asignado',
      3: 'En Camino',
      4: 'En Proceso',
      5: 'Terminado',
      6: 'En Espera',
      7: 'Cancelado'
    };
    return estadosMap[parseInt(ordenEstado)] || 'Desconocido';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BuildIcon />
          <Typography variant="h6">Gestión Dinámica de Servicios</Typography>
          <Chip 
            label={`Total: ${simboloMoneda} ${calcularTotal().toFixed(2)}`}
            color="success"
            icon={<CalculateIcon />}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

{!puedeModificar && (
  <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
    Solo se pueden modificar servicios en estados <strong>Pendiente (1)</strong> y <strong>Asignado (2)</strong>.<br/>
    Estado actual: <strong>{getEstadoText()} ({ordenEstado})</strong>
  </Alert>
)}

        {/* Tabla de servicios actuales */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Servicios Incluidos ({servicios.length})
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Servicio</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio Unit.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                  {puedeModificar && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {servicios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={puedeModificar ? 6 : 5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No hay servicios agregados. Utiliza el formulario inferior para agregar servicios.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  servicios.map((servicio, index) => (
                    <ServicioRow
                      key={`${servicio.id_servicio}-${index}`}
                      servicio={servicio}
                      index={index}
                      isEditing={editingIndex === index}
                      puedeModificar={puedeModificar}
                      onEdit={iniciarEdicion}
                      onSave={guardarEdicion}
                      onCancel={cancelarEdicion}
                      onDelete={eliminarServicio}
                      simboloMoneda={simboloMoneda}
                    />
                  ))
                )}
                
                {/* Fila de total */}
                {servicios.length > 0 && (
                  <TableRow sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        TOTAL
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                        {simboloMoneda} {calcularTotal().toFixed(2)}
                      </Typography>
                    </TableCell>
                    {puedeModificar && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Formulario para agregar nuevo servicio */}
        {puedeModificar && (
          <Card sx={{ bgcolor: 'rgba(26, 35, 126, 0.02)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1A237E', fontWeight: 'bold' }}>
                <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Agregar Nuevo Servicio
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Seleccionar Servicio</InputLabel>
                    <Select
                      value={nuevoServicio.id_servicio}
                      onChange={(e) => handleServicioChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Seleccione un servicio</em>
                      </MenuItem>
                      {serviciosDisponibles.map(servicio => (
                        <MenuItem key={servicio.id_servicio} value={servicio.id_servicio}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {servicio.nombre_servicio}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {simboloMoneda} {parseFloat(servicio.precio).toFixed(2)} - {servicio.descripcion_servicio}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cantidad"
                    type="number"
                    value={nuevoServicio.cantidad}
                    onChange={(e) => setNuevoServicio({
                      ...nuevoServicio, 
                      cantidad: parseInt(e.target.value) || 1
                    })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Precio"
                    type="number"
                    value={nuevoServicio.precio}
                    onChange={(e) => setNuevoServicio({
                      ...nuevoServicio, 
                      precio: parseFloat(e.target.value) || 0
                    })}
                    InputProps={{
                      startAdornment: `${simboloMoneda} `
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={9} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Observaciones"
                    value={nuevoServicio.detalle_glosa}
                    onChange={(e) => setNuevoServicio({
                      ...nuevoServicio, 
                      detalle_glosa: e.target.value
                    })}
                    placeholder="Detalles adicionales..."
                  />
                </Grid>
                
                <Grid item xs={3} md={1}>
                  <Tooltip title="Agregar servicio">
                    <Fab
                      size="small"
                      color="primary"
                      onClick={agregarServicio}
                      disabled={!nuevoServicio.id_servicio || loading}
                    >
                      {loading ? <CircularProgress size={20} /> : <AddIcon />}
                    </Fab>
                  </Tooltip>
                </Grid>
              </Grid>

              {nuevoServicio.id_servicio && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Subtotal: {simboloMoneda} {(nuevoServicio.cantidad * nuevoServicio.precio).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
        <Button 
          onClick={guardarCambios}
          variant="contained"
          disabled={loading}
        >
          Finalizar Gestión
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para cada fila de servicio
const ServicioRow = ({
  servicio,
  index,
  isEditing,
  puedeModificar,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  simboloMoneda = 'S/'
}) => {
  const [editData, setEditData] = useState({
    cantidad: servicio.cantidad,
    precio: servicio.precio,
    detalle_glosa: servicio.detalle_glosa || ''
  });

  useEffect(() => {
    if (isEditing) {
      setEditData({
        cantidad: servicio.cantidad,
        precio: servicio.precio,
        detalle_glosa: servicio.detalle_glosa || ''
      });
    }
  }, [isEditing, servicio]);

  const handleSave = () => {
    onSave(index, editData);
  };

  if (isEditing) {
    return (
      <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {servicio.nombre_servicio}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {servicio.descripcion_servicio}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editData.cantidad}
            onChange={(e) => setEditData({
              ...editData, 
              cantidad: parseInt(e.target.value) || 1
            })}
            sx={{ width: 80 }}
            inputProps={{ min: 1 }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editData.precio}
            onChange={(e) => setEditData({
              ...editData, 
              precio: parseFloat(e.target.value) || 0
            })}
            sx={{ width: 100 }}
            InputProps={{ startAdornment: `${simboloMoneda} ` }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            value={editData.detalle_glosa}
            onChange={(e) => setEditData({
              ...editData, 
              detalle_glosa: e.target.value
            })}
            sx={{ width: '100%' }}
            placeholder="Observaciones..."
          />
        </TableCell>
        <TableCell align="right">
          <Typography sx={{ fontWeight: 'bold', color: '#1A237E' }}>
            {simboloMoneda} {(editData.cantidad * editData.precio).toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Guardar cambios">
              <IconButton size="small" onClick={handleSave} color="primary">
                <SaveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar edición">
              <IconButton size="small" onClick={onCancel} color="secondary">
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ 
      '&:hover': { bgcolor: 'rgba(79, 195, 247, 0.08)' }
    }}>
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {servicio.nombre_servicio}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {servicio.descripcion_servicio}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="right">{servicio.cantidad}</TableCell>
      <TableCell align="right">{simboloMoneda} {parseFloat(servicio.precio).toFixed(2)}</TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {servicio.detalle_glosa || '—'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography sx={{ fontWeight: 'bold' }}>
          {simboloMoneda} {(servicio.cantidad * servicio.precio).toFixed(2)}
        </Typography>
      </TableCell>
      {puedeModificar && (
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Editar servicio">
              <IconButton
                size="small"
                onClick={() => onEdit(index)}
                sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar servicio">
              <IconButton
                size="small"
                onClick={() => onDelete(servicio, index)}
                sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

export default GestionServiciosDialog;